import type { MyPayEnvironment } from "../../generated/core-api";
import { useMyPayEnvironmentSelect } from "../hooks/useMyPayEnvironmentSelect";
import s from "./MyPayEnvironmentSelect.module.css";

export interface MyPayEnvironmentSelectProps {
  value: MyPayEnvironment | null;
  onChange: (environment: MyPayEnvironment | null) => void;
  /** Prevent environment changes while the owning workflow is busy. */
  disabled?: boolean;
  /** Auto-select when the API returns exactly one environment. Default true. */
  autoSelectSingle?: boolean;
  /** Visible product spelling. Defaults to the existing UI copy. */
  productName?: string;
  className?: string;
}

function withProductSpelling(value: string, productName: string): string {
  return value.replace(/\bdatapay\b/gi, productName);
}

/**
 * Selector for the target MyPay API deployment. Options come from
 * `GET /api/v1/onboarding/mypay-environments`.
 */
export function MyPayEnvironmentSelect({
  value,
  onChange,
  disabled = false,
  autoSelectSingle = true,
  productName = "Datapay",
  className,
}: MyPayEnvironmentSelectProps) {
  const { data: environments, isLoading, isError } = useMyPayEnvironmentSelect({
    value,
    onChange,
    autoSelectSingle,
  });

  if (isLoading) {
    return <p className={s.hint}>Loading {productName} environments…</p>;
  }

  if (isError) {
    return (
      <p className={s.error} role="alert">
        Could not load {productName} environments.
      </p>
    );
  }

  if (!environments || environments.length === 0) {
    return (
      <p className={s.error} role="alert">
        No {productName} environments are configured for this deployment.
      </p>
    );
  }

  if (environments.length === 1) {
    const only = environments[0];
    return (
      <p className={s.singleEnvironment}>
        {productName} environment:{" "}
        <strong>{withProductSpelling(only.name, productName)}</strong>
      </p>
    );
  }

  return (
    <select
      className={className ?? s.select}
      aria-label={`Select ${productName} environment`}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) =>
        onChange(
          e.target.value === "" ? null : (e.target.value as MyPayEnvironment)
        )
      }
    >
      <option value="">— Select environment —</option>
      {environments.map((entry) => (
        <option key={entry.environment} value={entry.environment}>
          {withProductSpelling(entry.name, productName)}
        </option>
      ))}
    </select>
  );
}
